import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
  Collapse,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import FilterListIcon from '@mui/icons-material/FilterList';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import TableChartIcon from '@mui/icons-material/TableChart';
import DataObjectIcon from '@mui/icons-material/DataObject';
import dayjs from 'dayjs';

import {
  ActionItem,
  Process,
  Unit,
  Lease,
} from '@urbanmix-tech/shared-js';

import { convertPseudoDateToRealDate } from '@/helpers/common';
import {
  calculateLeaseLength,
  calculateTotalFees,
  calculateTotalConcessions,
  calculateEffectiveMonthlyRent,
  calculateAnnualRentPerSqft,
  calculateEffectiveAnnualRentPerSqft,
} from '@/helpers/leaseHelpers';
import {
  exportLeasesToSpreadsheetStandard,
  exportLeasesToSpreadsheetRaw,
} from '@/helpers/spreadsheetExport';

// Redux
import { useDispatch, useSelector } from 'react-redux';
import {
  setHighlightedUnits,
  setSelectedUnit,
  setHoveredUnitId,
  setBuildingVisual,
} from '@/features/project/projectSlice';
import {
  ColorGradeVisualizer,
} from '@urbanmix-tech/um-embedded';

import { GenericDistinctVisualizer, colorScales } from './BuildingVisualizers';
import type { ColorScaleOptions } from './BuildingVisualizers';
import { LeaseCard } from './LeaseCard';
import { OccupancyStatsHeader } from './OccupancyStatsHeader';
import { selectUnitStatusById } from '@/features/project/unitStatusSelectors';
import { UNIT_STATUS_GROUPS, unitStatusGroup, type UnitStatusGroup } from '@/helpers/unitStatus';

type LeaseAnalysisTabContentProps = {
  units: Unit[];
  leases?: Lease[];
  actionItems: ActionItem[];
  hoveredUnitId: string | null;
  onSelectUnit(unitId: string | null): void;
  onHoverUnit(unitId: string | null): void;
  onLeaseUpdated?: (lease: Lease) => void;
};

export function LeaseAnalysisTabContent({
  units,
  leases,
  actionItems,
  hoveredUnitId,
  onSelectUnit,
  onHoverUnit,
  onLeaseUpdated,
}: LeaseAnalysisTabContentProps) {
  const ref = useRef<HTMLDivElement>(null);

  const [expiresInDays, setExpiresInDays] = useState(999);
  const [selectedAffordabilityType, setSelectedAffordabilityType] = useState<string[]>(['affordable', 'market']);
  const [selectedLeaseStatuses, setSelectedLeaseStatuses] = useState<string[]>(['valid', 'cancelled', 'draft']);
  const [selectedDateRange, setSelectedDateRange] = useState<string[]>(['active', 'future', 'past']);
  const [selectedScale] = useState<ColorScaleOptions>('default');
  const [visualizationField, setVisualizationField] = useState<'daysToExpire' | 'rent' | 'deposit' | 'arSqft' | 'concessions' | 'leaseLength' | 'effectiveMonthlyRent' | 'effectiveAnnualRentPerSqft' | null>('daysToExpire');
  const [sortBy, setSortBy] = useState<'daysToExpire' | 'rent' | 'unit' | 'sqft' | 'deposit' | 'arSqft' | 'concessions' | 'leaseLength' | 'effectiveMonthlyRent' | 'effectiveAnnualRentPerSqft'>('daysToExpire');
  const [selectedBedrooms, setSelectedBedrooms] = useState<number | null>(null);
  const [selectedLine, setSelectedLine] = useState<string | null>(null);
  const [selectedUnitStatuses, setSelectedUnitStatuses] = useState<UnitStatusGroup[]>([]);
  const [filtersExpanded, setFiltersExpanded] = useState(true);
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(null);

  // Mount Redux dispatcher
  const dispatch = useDispatch();

  const processes: Process[] = useSelector((state: any) => state.project.processes);
  const statusById = useSelector(selectUnitStatusById);

  // Create a map of unit ID to unit for quick lookup
  const unitMap = useMemo(() => {
    const map = new Map<string, Unit>();
    units.forEach((unit) => map.set(unit.id, unit));
    return map;
  }, [units]);

  // Create a map of units that have draft leases
  const unitsDraftLeaseMap = useMemo(() => {
    if (!leases) return new Set<string>();
    const draftUnitIds = new Set<string>();
    leases.forEach((lease) => {
      if (lease.status === 'draft') {
        draftUnitIds.add(lease.unitId);
      }
    });
    return draftUnitIds;
  }, [leases]);

  // Get unique bedroom counts for filter dropdown
  const uniqueBedrooms = useMemo(() => {
    const bedrooms = new Set(units.map((u) => u.numBedrooms));
    return Array.from(bedrooms).sort((a, b) => a - b);
  }, [units]);

  // Get unique lines for filter dropdown (use type or line, whichever has value)
  const uniqueLines = useMemo(() => {
    const lines = new Set(
      units
        .map((u) => u.type || u.line)
        .filter((l) => l && l.trim())
    );
    return Array.from(lines).sort();
  }, [units]);

  // Count active filters
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (expiresInDays !== 999) count++;
    if (selectedAffordabilityType.length < 2) count++;
    if (selectedLeaseStatuses.length < 3) count++;
    if (selectedDateRange.length < 3) count++;
    if (selectedBedrooms !== null) count++;
    if (selectedLine !== null) count++;
    if (selectedUnitStatuses.length > 0) count++;
    return count;
  }, [expiresInDays, selectedAffordabilityType, selectedLeaseStatuses, selectedDateRange, selectedBedrooms, selectedLine, selectedUnitStatuses]);

  // Clear all filters
  const clearAllFilters = useCallback(() => {
    setExpiresInDays(999);
    setSelectedAffordabilityType(['affordable', 'market']);
    setSelectedLeaseStatuses(['valid', 'cancelled', 'draft']);
    setSelectedDateRange(['active', 'future', 'past']);
    setSelectedBedrooms(null);
    setSelectedLine(null);
    setSelectedUnitStatuses([]);
  }, []);

  // Export menu handlers
  const handleExportClick = (event: React.MouseEvent<HTMLElement>) => {
    setExportMenuAnchor(event.currentTarget);
  };

  const handleExportClose = () => {
    setExportMenuAnchor(null);
  };

  const handleExportStandard = async () => {
    handleExportClose();
    if (filteredLeases.length === 0) return;
    await exportLeasesToSpreadsheetStandard(filteredLeases, unitMap, undefined, statusById);
  };

  const handleExportRaw = async () => {
    handleExportClose();
    if (filteredLeases.length === 0) return;
    await exportLeasesToSpreadsheetRaw(filteredLeases, unitMap);
  };

  // Helper to safely convert lease dates
  const getLeaseEndDate = (lease: Lease): Date | null => {
    return convertPseudoDateToRealDate(lease.leaseEndDate as any);
  };

  // Filter leases based on expiry and affordable status
  const filteredLeases = useMemo(() => {
    if (!leases) return [];
    const now = dayjs().startOf('day');

    return leases.filter((lease) => {
      // Filter by lease status
      const leaseStatus = lease.status || 'valid';
      if (!selectedLeaseStatuses.includes(leaseStatus)) return false;

      // Filter by date range (active, future, past)
      const startDate = convertPseudoDateToRealDate(lease.leaseStartDate as any);
      const endDate = getLeaseEndDate(lease);
      if (!endDate) return false;

      const startDayjs = startDate ? dayjs(startDate).startOf('day') : null;
      const endDayjs = dayjs(endDate).startOf('day');

      const isActive = startDayjs && startDayjs.isBefore(now) && endDayjs.isAfter(now);
      const isFuture = startDayjs && startDayjs.isAfter(now);
      const isPast = endDayjs.isBefore(now);

      if (!selectedDateRange.includes('active') && isActive) return false;
      if (!selectedDateRange.includes('future') && isFuture) return false;
      if (!selectedDateRange.includes('past') && isPast) return false;
      if (selectedDateRange.length > 0 && !isActive && !isFuture && !isPast) return false;

      // Filter by days to expire
      const daysToExpire = dayjs(endDate).diff(now, 'day');
      // Only show leases that expire within the specified days
      if (daysToExpire > expiresInDays) return false;

      // Filter by affordable/market status from unit
      const unit = unitMap.get(lease.unitId);
      if (!unit) return false;

      const affordabilityType = unit.affordable ? 'affordable' : 'market';
      if (!selectedAffordabilityType.includes(affordabilityType)) return false;

      // Filter by bedrooms
      if (selectedBedrooms !== null && unit.numBedrooms !== selectedBedrooms) return false;

      // Filter by line (use type or line, whichever has value)
      if (selectedLine !== null && (unit.type || unit.line) !== selectedLine) return false;

      // Filter by live unit status (empty selection = no filter)
      if (selectedUnitStatuses.length > 0) {
        const status = statusById[unit.id];
        if (!status || !selectedUnitStatuses.includes(unitStatusGroup(status))) return false;
      }

      return true;
    });
  }, [leases, expiresInDays, selectedAffordabilityType, selectedLeaseStatuses, selectedDateRange, unitMap, selectedBedrooms, selectedLine, selectedUnitStatuses, statusById]);

  // Create visualization data from filtered leases using helper functions
  const visualizationData = useMemo(() => {
    return filteredLeases
      .map((lease) => {
        const unit = unitMap.get(lease.unitId);
        if (!unit) return null;

        const endDate = getLeaseEndDate(lease);
        if (!endDate) return null;

        const daysToExpire = dayjs(endDate).diff(dayjs(), 'day');
        const sqft = unit.sqft || 0;
        const leaseLength = calculateLeaseLength(lease);
        const totalFees = calculateTotalFees(lease);
        const totalConcessions = calculateTotalConcessions(lease);
        const effectiveMonthlyRent = calculateEffectiveMonthlyRent(lease);
        const annualRentPerSqft = calculateAnnualRentPerSqft(lease, sqft);
        const effectiveAnnualRentPerSqft = calculateEffectiveAnnualRentPerSqft(lease, sqft);

        return {
          lease,
          unit,
          daysToExpire,
          values: {
            rent: lease.monthlyRent,
            sqft,
            annualRentPerSqft,
            totalFees,
            totalConcessions,
            leaseLength,
            effectiveMonthlyRent,
            effectiveAnnualRentPerSqft,
          },
        };
      })
      .filter(Boolean) as Array<{
        lease: Lease;
        unit: Unit;
        daysToExpire: number;
        values: {
          rent: number;
          sqft: number;
          annualRentPerSqft: number;
          totalFees: number;
          totalConcessions: number;
          leaseLength: number;
          effectiveMonthlyRent: number;
          effectiveAnnualRentPerSqft: number;
        };
      }>;
  }, [filteredLeases, unitMap]);

  // Handle visualization based on selected field
  useEffect(() => {
    if (!visualizationField || visualizationData.length === 0) {
      dispatch(
        setBuildingVisual({
          apartmentData: [],
          visualizer: null,
        })
      );
      return;
    }

    let values = visualizationData.map((item) => {
      switch (visualizationField) {
        case 'daysToExpire':
          return item.daysToExpire;
        case 'rent':
          return item.values.rent;
        case 'deposit':
          return item.lease.securityDeposit || 0;
        case 'arSqft':
          return item.values.annualRentPerSqft;
        case 'concessions':
          return item.values.totalConcessions;
        case 'leaseLength':
          return item.values.leaseLength;
        case 'effectiveMonthlyRent':
          return item.values.effectiveMonthlyRent;
        case 'effectiveAnnualRentPerSqft':
          return item.values.effectiveAnnualRentPerSqft;
        default:
          return 0;
      }
    });

    const distinctValues = new Set(values);
    const colors = colorScales[selectedScale];

    let visualizer = null;
    if (distinctValues.size < 10) {
      let colorMap = new Map();
      let arrDistinctValues = Array.from(distinctValues);
      arrDistinctValues.forEach((value, index) => {
        colorMap.set(value, colors[index % colors.length]);
      });
      visualizer = new GenericDistinctVisualizer(colorMap);
    } else {
      // Filter out zero values for accurate min/max calculation
      const nonZeroValues = values.filter((v) => v !== 0);
      const validValues = nonZeroValues.length > 0 ? nonZeroValues : values;

      const minValue = Math.min(...validValues);
      const maxValue = Math.max(...validValues);
      visualizer = new ColorGradeVisualizer(colors, minValue, maxValue);
    }

    let data = visualizationData.map((item) => {
      let value: number = 0;
      let label = '';
      switch (visualizationField) {
        case 'daysToExpire':
          value = item.daysToExpire;
          label = 'Days to Expiry';
          break;
        case 'rent':
          value = item.values.rent;
          label = 'Monthly Rent';
          break;
        case 'deposit':
          value = item.lease.securityDeposit || 0;
          label = 'Security Deposit';
          break;
        case 'arSqft':
          value = item.values.annualRentPerSqft;
          label = 'Annual Rent/Sqft';
          break;
        case 'concessions':
          value = item.values.totalConcessions;
          label = 'Concessions';
          break;
        case 'leaseLength':
          value = item.values.leaseLength;
          label = 'Lease Length (months)';
          break;
        case 'effectiveMonthlyRent':
          value = item.values.effectiveMonthlyRent;
          label = 'Effective Monthly Rent';
          break;
        case 'effectiveAnnualRentPerSqft':
          value = item.values.effectiveAnnualRentPerSqft;
          label = 'Effective Annual Rent/Sqft';
          break;
      }
      return {
        apartmentId: item.unit.id,
        value,
        label,
      };
    });

    dispatch(
      setBuildingVisual({
        apartmentData: data,
        visualizer: visualizer,
      })
    );
  }, [visualizationField, selectedScale, visualizationData, dispatch]);

  // Sort the visualization data based on selected sort field
  const sortedVisualizationData = useMemo(() => {
    return [...visualizationData].sort((a, b) => {
      switch (sortBy) {
        case 'daysToExpire':
          return a.daysToExpire - b.daysToExpire;
        case 'rent':
          return a.values.rent - b.values.rent;
        case 'unit':
          return a.unit.name?.localeCompare(b.unit.name || '') || 0;
        case 'sqft':
          return a.values.sqft - b.values.sqft;
        case 'deposit':
          return (a.lease.securityDeposit || 0) - (b.lease.securityDeposit || 0);
        case 'arSqft':
          return a.values.annualRentPerSqft - b.values.annualRentPerSqft;
        case 'concessions':
          return a.values.totalConcessions - b.values.totalConcessions;
        case 'leaseLength':
          return a.values.leaseLength - b.values.leaseLength;
        case 'effectiveMonthlyRent':
          return a.values.effectiveMonthlyRent - b.values.effectiveMonthlyRent;
        case 'effectiveAnnualRentPerSqft':
          return a.values.effectiveAnnualRentPerSqft - b.values.effectiveAnnualRentPerSqft;
        default:
          return 0;
      }
    });
  }, [visualizationData, sortBy]);

  return (
    <div className="lease-analysis h-[calc(100vh-112px)] w-full flex flex-col p-[15px] overflow-hidden" ref={ref}>
      <OccupancyStatsHeader />
      {/* Collapsible Filters Header */}
      <Box
        className="flex items-center gap-2 mb-2 cursor-pointer select-none"
        onClick={() => setFiltersExpanded(!filtersExpanded)}
      >
        <IconButton
          size="small"
          sx={{ p: 0, mr: 0.5 }}
        >
          {filtersExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
        <FilterListIcon fontSize="small" color="action" />
        <Typography variant="subtitle2" color="textSecondary">
          Filters
        </Typography>
        {activeFilterCount > 0 && (
          <Chip
            label={activeFilterCount}
            size="small"
            color="primary"
            sx={{ height: 20, minWidth: 20 }}
          />
        )}
        {activeFilterCount > 0 && (
          <Button
            size="small"
            variant="text"
            startIcon={<ClearIcon />}
            onClick={(e) => {
              e.stopPropagation();
              clearAllFilters();
            }}
            sx={{ textTransform: 'none', ml: 'auto' }}
          >
            Clear All
          </Button>
        )}
        <Box className="flex-1" />
        <Typography variant="body2" color="textSecondary">
          {filteredLeases.length} lease{filteredLeases.length !== 1 ? 's' : ''} found
        </Typography>
        {filteredLeases.length > 0 && (
          <Tooltip title="Export to Excel">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleExportClick(e);
              }}
              sx={{ ml: 1 }}
            >
              <FileDownloadIcon fontSize="small" color="action" />
            </IconButton>
          </Tooltip>
        )}
        <Menu
          anchorEl={exportMenuAnchor}
          open={Boolean(exportMenuAnchor)}
          onClose={handleExportClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <MenuItem onClick={handleExportStandard}>
            <ListItemIcon>
              <TableChartIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary="Standard Format"
              secondary="Processed lease data with calculated fields"
            />
          </MenuItem>
          <MenuItem onClick={handleExportRaw}>
            <ListItemIcon>
              <DataObjectIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary="Raw Data"
              secondary="Original eLease fields from DocuSign"
            />
          </MenuItem>
        </Menu>
      </Box>

      {/* Collapsible Filters Content */}
      <Collapse in={filtersExpanded}>
        <Stack direction="row" gap={1} mb={2} alignItems="center" flexWrap="wrap">
          {/* Expires In Days Filter */}
          <FormControl size="small" variant="standard" sx={{ minWidth: 140 }}>
            <InputLabel>Expires In Days</InputLabel>
            <Select
              value={expiresInDays}
              onChange={(e) => setExpiresInDays(e.target.value as number)}
              label="Expires In Days"
            >
              <MenuItem value={30}>30 Days</MenuItem>
              <MenuItem value={60}>60 Days</MenuItem>
              <MenuItem value={90}>90 Days</MenuItem>
              <MenuItem value={120}>120 Days</MenuItem>
              <MenuItem value={180}>180 Days</MenuItem>
              <MenuItem value={365}>365 Days</MenuItem>
              <MenuItem value={999}>All Leases</MenuItem>
            </Select>
          </FormControl>

          {/* Affordability Type Filter */}
          <FormControl size="small" variant="standard" sx={{ minWidth: 140 }}>
            <InputLabel>Type</InputLabel>
            <Select
              multiple
              value={selectedAffordabilityType}
              onChange={(e) => setSelectedAffordabilityType(e.target.value as string[])}
              displayEmpty
              renderValue={(selected) =>
                selected.length === 0 ? 'Type' :
                selected.length === 1 ? (selected[0].charAt(0).toUpperCase() + selected[0].slice(1)) :
                `Type (${selected.length})`
              }
              label="Type"
            >
              <MenuItem value="affordable">
                <Checkbox checked={selectedAffordabilityType.includes('affordable')} size="small" />
                <Typography variant="body2">Affordable</Typography>
              </MenuItem>
              <MenuItem value="market">
                <Checkbox checked={selectedAffordabilityType.includes('market')} size="small" />
                <Typography variant="body2">Market</Typography>
              </MenuItem>
            </Select>
          </FormControl>

          {/* Bedrooms Filter */}
          <FormControl size="small" variant="standard" sx={{ minWidth: 100 }}>
            <InputLabel>Bedrooms</InputLabel>
            <Select
              value={selectedBedrooms ?? ''}
              onChange={(e) => setSelectedBedrooms(e.target.value === '' ? null : (e.target.value as number))}
              label="Bedrooms"
            >
              <MenuItem value="">All</MenuItem>
              {uniqueBedrooms.map((br) => (
                <MenuItem key={br} value={br}>
                  {br} Bed{br !== 1 ? 's' : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Line/Type Filter */}
          {uniqueLines.length > 0 && (
            <FormControl size="small" variant="standard" sx={{ minWidth: 100 }}>
              <InputLabel>Line/Type</InputLabel>
              <Select
                value={selectedLine ?? ''}
                onChange={(e) => setSelectedLine(e.target.value === '' ? null : (e.target.value as string))}
                label="Line/Type"
              >
                <MenuItem value="">All</MenuItem>
                {uniqueLines.map((line) => (
                  <MenuItem key={line} value={line}>
                    {line}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Unit Status Filter (folded from the state_* feed) */}
          <FormControl size="small" variant="standard" sx={{ minWidth: 150 }}>
            <InputLabel>Unit Status</InputLabel>
            <Select
              multiple
              value={selectedUnitStatuses}
              onChange={(e) => setSelectedUnitStatuses(e.target.value as UnitStatusGroup[])}
              displayEmpty
              renderValue={(selected) =>
                selected.length === 0 ? 'All' :
                selected.length === 1 ? selected[0] :
                `Unit Status (${selected.length})`
              }
              label="Unit Status"
            >
              {UNIT_STATUS_GROUPS.map((group) => (
                <MenuItem key={group} value={group}>
                  <Checkbox checked={selectedUnitStatuses.includes(group)} size="small" />
                  <Typography variant="body2">{group}</Typography>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Sort By Filter */}
          <FormControl size="small" variant="standard" sx={{ minWidth: 140 }}>
            <InputLabel>Sort By</InputLabel>
            <Select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
              label="Sort By"
            >
              <MenuItem value="daysToExpire">Days to Expiry</MenuItem>
              <MenuItem value="rent">Monthly Rent</MenuItem>
              <MenuItem value="unit">Unit Name</MenuItem>
              <MenuItem value="sqft">Square Footage</MenuItem>
              <MenuItem value="deposit">Security Deposit</MenuItem>
              <MenuItem value="arSqft">Annual Rent/Sqft</MenuItem>
              <MenuItem value="concessions">Concessions</MenuItem>
              <MenuItem value="leaseLength">Lease Length</MenuItem>
              <MenuItem value="effectiveMonthlyRent">Effective Monthly Rent</MenuItem>
              <MenuItem value="effectiveAnnualRentPerSqft">Effective Annual Rent/Sqft</MenuItem>
            </Select>
          </FormControl>

          {/* Lease Status Filter */}
          <FormControl size="small" variant="standard" sx={{ minWidth: 130 }}>
            <InputLabel>Status</InputLabel>
            <Select
              multiple
              value={selectedLeaseStatuses}
              onChange={(e) => setSelectedLeaseStatuses(e.target.value as string[])}
              displayEmpty
              renderValue={(selected) =>
                selected.length === 0 ? 'Status' :
                selected.length === 1 ? (selected[0].charAt(0).toUpperCase() + selected[0].slice(1)) :
                `Status (${selected.length})`
              }
              label="Status"
            >
              <MenuItem value="valid">
                <Checkbox checked={selectedLeaseStatuses.includes('valid')} size="small" />
                <Typography variant="body2">Valid</Typography>
              </MenuItem>
              <MenuItem value="cancelled">
                <Checkbox checked={selectedLeaseStatuses.includes('cancelled')} size="small" />
                <Typography variant="body2">Cancelled</Typography>
              </MenuItem>
              <MenuItem value="draft">
                <Checkbox checked={selectedLeaseStatuses.includes('draft')} size="small" />
                <Typography variant="body2">Draft</Typography>
              </MenuItem>
            </Select>
          </FormControl>

          {/* Date Range Filter (Active/Future/Past) */}
          <FormControl size="small" variant="standard" sx={{ minWidth: 130 }}>
            <InputLabel>Lease Period</InputLabel>
            <Select
              multiple
              value={selectedDateRange}
              onChange={(e) => setSelectedDateRange(e.target.value as string[])}
              displayEmpty
              renderValue={(selected) =>
                selected.length === 0 ? 'Lease Period' :
                selected.length === 1 ? (selected[0].charAt(0).toUpperCase() + selected[0].slice(1)) :
                `Lease Period (${selected.length})`
              }
              label="Lease Period"
            >
              <MenuItem value="active">
                <Checkbox checked={selectedDateRange.includes('active')} size="small" />
                <Typography variant="body2">Active</Typography>
              </MenuItem>
              <MenuItem value="future">
                <Checkbox checked={selectedDateRange.includes('future')} size="small" />
                <Typography variant="body2">Future</Typography>
              </MenuItem>
              <MenuItem value="past">
                <Checkbox checked={selectedDateRange.includes('past')} size="small" />
                <Typography variant="body2">Past</Typography>
              </MenuItem>
            </Select>
          </FormControl>

          {/* Visualization Filter */}
          <FormControl size="small" variant="standard" sx={{ minWidth: 140 }}>
            <InputLabel>Visualization</InputLabel>
            <Select
              value={visualizationField ?? ''}
              onChange={(e) => setVisualizationField(e.target.value === '' ? null : e.target.value as typeof visualizationField)}
              label="Visualization"
            >
              <MenuItem value="daysToExpire">Days to Expiry</MenuItem>
              <MenuItem value="rent">Monthly Rent</MenuItem>
              <MenuItem value="deposit">Security Deposit</MenuItem>
              <MenuItem value="arSqft">Annual Rent/Sqft</MenuItem>
              <MenuItem value="concessions">Concessions</MenuItem>
              <MenuItem value="leaseLength">Lease Length</MenuItem>
              <MenuItem value="effectiveMonthlyRent">Effective Monthly Rent</MenuItem>
              <MenuItem value="effectiveAnnualRentPerSqft">Effective Annual Rent/Sqft</MenuItem>
              <MenuItem value="">None</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </Collapse>

      {/* Lease Cards */}
      <div className="flex-1 overflow-y-auto flex flex-col gap-12">
        {filteredLeases.length > 0 ? (
          sortedVisualizationData.map((item) => (
            <LeaseCard
              key={item.lease.id}
              lease={item.lease}
              unit={item.unit}
              processes={processes}
              isHovered={hoveredUnitId === item.unit.id}
              onHoverUnit={onHoverUnit}
              onSelectUnit={onSelectUnit}
              showEdit={false}
              showCategory={false}
              onLeaseUpdated={onLeaseUpdated}
              hasDraftLease={unitsDraftLeaseMap.has(item.unit.id)}
            />
          ))
        ) : (
          <div className="flex items-center justify-center flex-1">
            <Typography variant="h6" color="textSecondary">
              No leases found matching the selected filters
            </Typography>
          </div>
        )}
      </div>
    </div>
  );
}
